// Step-up de MFA (`spec/17-frontend.md` §4.3). Existe uma vez só: quem o abre é
// `app/step-up.tsx`, chamado de dentro de `api/client.ts` quando a API devolve
// `403 mfa_required`. Nenhuma tela reimplementa isso.
//
// AUTH-05: o seller sem 2FA confirma com a SENHA (`POST .../step-up { password }`). Quem
// decide qual ramo aparece é o `two_factor_enabled` da sessão já carregada, nunca um
// header novo (`spec/17` §4.3 item 2).

import { useState } from 'react';
import { api } from '../../api/client.ts';
import { errorMessageOf } from '../../api/errors.ts';
import { surface } from '../../app/surface.ts';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';

export function StepUpDialog({
  open,
  twoFactorEnabled,
  onDone,
}: {
  open: boolean;
  /** `false` = seller sem 2FA: o fator é a senha atual. Admin é sempre `true`. */
  twoFactorEnabled: boolean;
  /** `true` = step-up confirmado; `api/client.ts` reexecuta a chamada original. */
  onDone: (verified: boolean) => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const close = (verified: boolean): void => {
    setCode('');
    setError(undefined);
    setSubmitting(false);
    onDone(verified);
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError(undefined);
    try {
      const path = surface() === 'admin' ? '/admin/v1/auth/step-up' : '/v1/me/auth/step-up';
      const value = code.trim();
      await api(path, {
        method: 'POST',
        body: twoFactorEnabled ? { code: value } : { password: value },
      });
      close(true);
    } catch (caught) {
      setError(errorMessageOf(caught));
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close(false);
        }
      }}
    >
      <DialogContent className="bg-raised sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {twoFactorEnabled ? 'Confirme com o segundo fator' : 'Confirme com sua senha'}
          </DialogTitle>
          <DialogDescription>
            {twoFactorEnabled
              ? 'Esta ação é sensível. Digite o código do seu app de autenticação ou um backup code.'
              : 'Esta ação é sensível. Digite a senha da sua conta para continuar.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="flex flex-col gap-2">
            {twoFactorEnabled ? (
              <>
                <Label htmlFor="step-up-code">Código</Label>
                <Input
                  id="step-up-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value);
                  }}
                  className="num h-11 text-16 tracking-[0.3em]"
                />
              </>
            ) : (
              <>
                <Label htmlFor="step-up-password">Senha</Label>
                <Input
                  id="step-up-password"
                  type="password"
                  autoComplete="current-password"
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value);
                  }}
                  className="h-11 text-16"
                />
              </>
            )}
          </div>
          {error !== undefined && (
            <p role="alert" className="text-12 text-danger">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                close(false);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || code.trim() === ''}>
              {submitting ? 'Verificando…' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
